/**
 * Pass-target selection — who a pass is FOR. (§4.2, Phase 3B)
 *
 * The human expresses intent with the aim; this module resolves that intent to
 * the most sensible teammate near it, the way real players pass to a MAN, not a
 * bearing. Candidates inside the assist cone are scored on:
 *
 *   - alignment with the aim (dominant — the human's expressed intent),
 *   - passing-lane clearance (a marked lane is a turnover, not a pass),
 *   - receiver freedom (space to take the touch),
 *   - offside position (an offside receiver is excluded outright — the same
 *     second-last-opponent test rules/offside.ts snapshots at the kick).
 *
 * PURE and read-only over WorldState: no state is written, no RNG drawn. The
 * sim calls it at strike time to bend the aim and scale the power; the client
 * calls the SAME function per frame to draw the pass-target indicator (3C) —
 * shared code, so what you see is literally what the sim will choose. Nothing
 * here is cloned, hashed or serialized.
 */
import type { WorldState } from './types.js';
import { MAX_PLAYERS } from './types.js';
import {
  PASS_ASSIST_CONE_DEG,
  PASS_ASSIST_HYSTERESIS,
  PASS_ASSIST_LANE_RADIUS,
  PASS_ASSIST_ALIGN_WEIGHT,
  PASS_ASSIST_LANE_WEIGHT,
  PASS_ASSIST_FREE_WEIGHT,
  PRESSURE_RADIUS,
  GOAL_WIDTH,
} from './constants.js';
import { DEG2RAD, dcos, dsin } from '../math/vec3.js';

/** True if the segment a→b crosses a goal line (x = ±halfLength) BETWEEN the
 *  posts — i.e. the ball would have to pass through the goal frame, which the
 *  net/posts physically block. A pass like that is impossible (directive §11). */
function laneCrossesGoalFrame(state: WorldState, ax: number, az: number, bx: number, bz: number): boolean {
  const half = state.config.pitchLength * 0.5;
  const postZ = GOAL_WIDTH * 0.5 + 0.6; // posts + a little frame margin
  for (const goalX of [half, -half]) {
    // Does the segment straddle this goal-line x?
    if ((ax - goalX) * (bx - goalX) >= 0) continue; // both same side (or on it)
    const t = (goalX - ax) / (bx - ax);
    const zAt = az + (bz - az) * t;
    if (Math.abs(zAt) <= postZ) return true; // crosses through the mouth/frame
  }
  return false;
}

function distXZ(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dz * dz);
}

/** Nearest opponent's distance to the segment striker→target — the lane. */
function laneClearance(state: WorldState, team: number, sx: number, sz: number, tx: number, tz: number, striker: number, target: number): number {
  const p = state.players;
  const dx = tx - sx;
  const dz = tz - sz;
  const len2 = dx * dx + dz * dz;
  let nearest = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === striker || i === target) continue;
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! === team) continue;
    let t = 0;
    if (len2 > 1e-9) {
      t = ((p.px[i]! - sx) * dx + (p.pz[i]! - sz) * dz) / len2;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }
    const d = distXZ(p.px[i]!, p.pz[i]!, sx + dx * t, sz + dz * t);
    if (d < nearest) nearest = d;
  }
  return nearest;
}

/** Nearest opponent to the receiver — the space he has to take a touch. */
function receiverFreedom(state: WorldState, team: number, tx: number, tz: number): number {
  const p = state.players;
  let nearest = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! === team) continue;
    const d = distXZ(tx, tz, p.px[i]!, p.pz[i]!);
    if (d < nearest) nearest = d;
  }
  return nearest >= PRESSURE_RADIUS ? 1 : nearest / PRESSURE_RADIUS;
}

/**
 * Is `id` in an offside POSITION right now? Same geometry rules/offside.ts
 * snapshots at the kick: opponent's half AND ahead of the ball AND ahead of the
 * second-last opponent. Read-only; used to EXCLUDE a candidate the pass would
 * flag the moment it is struck.
 */
function inOffsidePosition(state: WorldState, id: number): boolean {
  if (!state.config.offsideEnabled) return false;
  const p = state.players;
  const team = p.team[id]!;
  const dir = ((team === 0) === state.teamAAttacksPositiveX) ? 1 : -1;
  const half = state.config.pitchLength * 0.5;
  const proj = dir * p.px[id]!;
  if (proj <= 0) return false; // own half — never offside
  if (proj <= dir * state.ball.px) return false; // level with/behind the ball
  const defendingTeam = 1 - team;
  let firstProj = Number.NEGATIVE_INFINITY;
  let secondProj = Number.NEGATIVE_INFINITY;
  let defenders = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! !== defendingTeam) continue;
    defenders++;
    const dproj = dir * p.px[i]!;
    if (dproj >= firstProj) {
      secondProj = firstProj;
      firstProj = dproj;
    } else if (dproj > secondProj) {
      secondProj = dproj;
    }
  }
  if (defenders < 2) return false;
  if (half <= 0) return false;
  return proj > secondProj;
}

/** Score of one candidate, or −1 if it fails a hard gate (cone/offside/GK/…). */
function scoreCandidate(state: WorldState, striker: number, i: number, sx: number, sz: number, dirx: number, dirz: number, coneCos: number): number {
  const p = state.players;
  const team = p.team[striker]!;
  if (i === striker || p.active[i] !== 1 || p.sentOff[i] === 1) return -1;
  if (p.team[i]! !== team) return -1;
  if (state.positions[i] === 'GK') return -1; // never pass to your own keeper in attack (§10)
  if (p.knockdownTimer[i]! > 0) return -1; // fallen / unavailable (§10)
  const tx = p.px[i]!;
  const tz = p.pz[i]!;
  const dx = tx - sx;
  const dz = tz - sz;
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 1e-6) return -1;
  const align = (dx * dirx + dz * dirz) / len;
  if (align < coneCos) return -1; // outside the (broad) cone — not what the human meant
  if (inOffsidePosition(state, i)) return -1; // would be flagged at the kick
  if (laneCrossesGoalFrame(state, sx, sz, tx, tz)) return -1; // blocked by the goal frame

  const lane = laneClearance(state, team, sx, sz, tx, tz, striker, i);
  const laneNorm = lane >= PASS_ASSIST_LANE_RADIUS ? 1 : lane / PASS_ASSIST_LANE_RADIUS;
  const freedom = receiverFreedom(state, team, tx, tz);
  // Alignment dominates (the human's intent); the lane and the receiver's space
  // break ties toward the pass that actually arrives.
  return align * PASS_ASSIST_ALIGN_WEIGHT + laneNorm * PASS_ASSIST_LANE_WEIGHT + freedom * PASS_ASSIST_FREE_WEIGHT;
}

/**
 * The teammate this pass is FOR, or −1 when nobody sensible is near the aim
 * (the pass then flies the raw bearing — the human keeps full override).
 *
 * `prevTarget` is the currently-highlighted receiver (or −1). If it is still a
 * valid candidate and within PASS_ASSIST_HYSTERESIS of the new best, it KEEPS the
 * highlight — so the white marker does not flicker between two near-tie teammates
 * as the aim wobbles (directive §13). Pass −1 for a fresh, stateless choice (the
 * sim does this at strike time; the client threads its last target for stability).
 */
export function choosePassTarget(state: WorldState, striker: number, aimYaw: number, prevTarget = -1): number {
  const p = state.players;
  const sx = p.px[striker]!;
  const sz = p.pz[striker]!;
  const dirx = dcos(aimYaw);
  const dirz = dsin(aimYaw);
  const coneCos = dcos(PASS_ASSIST_CONE_DEG * DEG2RAD);

  let best = -1;
  let bestScore = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    const score = scoreCandidate(state, striker, i, sx, sz, dirx, dirz, coneCos);
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }

  // Hysteresis: keep the incumbent highlight unless clearly beaten.
  if (prevTarget >= 0 && prevTarget !== best) {
    const prevScore = scoreCandidate(state, striker, prevTarget, sx, sz, dirx, dirz, coneCos);
    if (prevScore >= 0 && prevScore + PASS_ASSIST_HYSTERESIS >= bestScore) return prevTarget;
  }
  return best;
}
